import logger from '../../Utils/logger';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import styles from "./Style.module.css";
import { useVoucherContext } from "../../Context/Voucher/VoucherContext";
import { useSticky } from "../../Utils/Helper";
import { useNavigate } from "react-router-dom";
import { Header } from "../../Components/Header/Header";
import VoucherDetails from "../../Components/VoucherDetails/VoucherDetails";
import { Footer } from "../../Components/Cart/Footer";
import TermsAndConditionsV2 from "../../Components/TermsAndConditionsV2/TermsAndConditionsV2";
import { Snakbar } from "../../Components/Snakbar/Snakbar";

export const ReviewVoucherPurchase = () => {
  const navigate = useNavigate();
  const {
    selectedVouchers,
    billingDetails,
    recipientDetails,
    giftMessage,
    orderData,
    orderMessage,
  } = useVoucherContext();

  const { isSticky, sentinelRef } = useSticky();
  const [showSuccess, setShowSuccess] = useState(!!orderMessage);
  const contentRef = useRef(null);
  const footerRef = useRef(null);

  useLayoutEffect(() => {
    const content = contentRef.current;
    const footer = footerRef.current;
    if (!content || !footer) return undefined;

    // Footer copy can wrap on narrow screens; reserve its rendered height,
    // including safe-area padding, instead of a fixed mobile spacer.
    const measureFooter = () => {
      content.style.setProperty("--ReviewFooterHeight", `${footer.offsetHeight}px`);
    };
    // Writing layout inside the ResizeObserver callback re-triggers observation
    // in the same frame ("ResizeObserver loop completed with undelivered
    // notifications"); defer the write to the next frame instead.
    let frame = null;
    const scheduleMeasure = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        measureFooter();
      });
    };
    measureFooter();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(scheduleMeasure) : null;
    observer?.observe(footer);
    window.addEventListener("resize", scheduleMeasure);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", scheduleMeasure);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, []);

  // Redirect if no vouchers selected
  useEffect(() => {
    if (!selectedVouchers || selectedVouchers.length === 0) {
      navigate("/select-vouchers");
    }
  }, [selectedVouchers, navigate]);

  useEffect(() => {
    if (!orderMessage) return;

    setShowSuccess(true);
    const timer = setTimeout(() => {
      setShowSuccess(false);
    }, 5000);

    return () => clearTimeout(timer);
  }, [orderMessage]);

  // Use API total_price as the authoritative amount
  const apiTotalPrice = orderData?.order?.total_price;
  const totalAmount = Number(apiTotalPrice || 0).toFixed(2);
  const displayedGiftMessage = giftMessage?.trim();

  const handlePaymentClick = useCallback(() => {
    if (orderData) {
      navigate("/payment-options", {
        state: {
          voucherOrderData: orderData,
          amount: apiTotalPrice,
          isVoucherPayment: true,
        },
      });
    } else {
      logger.warn("⚠️ No orderData available for payment");
    }
  }, [orderData, apiTotalPrice, navigate]);

  return (
    <>
      <div className="sticky-sentinel" ref={sentinelRef} aria-hidden="true" />
      <div className={`sticky-header ${isSticky ? "is-sticky" : ""}`}>
        <Header title="Review Particulars" />
      </div>
      <div
        ref={contentRef}
        className={`${styles.reviewAndConfirmBooking} container-md space-x space-y`}
      >
        <section className={styles.reviewBillingDetails}>
          <h2>Billing Details</h2>
          <p>{billingDetails.name || "—"}</p>
          <p>{billingDetails.mobile || "—"}</p>
          <p>{billingDetails.email || "—"}</p>
        </section>
        <div className="line" />
        <section className={styles.reviewBillingDetails}>
          <h2>Recipient Details</h2>
          <p>{recipientDetails.name || "—"}</p>
          <p>{recipientDetails.mobile || "—"}</p>
          <p>{recipientDetails.email || "—"}</p>
        </section>
        <div className="line" />
        <section className={styles.reviewBillingDetails}>
          <h2>Gift Message</h2>
          <p
            className={
              displayedGiftMessage ? styles.giftMessage : styles.emptyValue
            }
          >
            {displayedGiftMessage || "Not provided"}
          </p>
        </section>
        <div className="line" />
        <section className={styles.reviewBillingDetails}>
          <h2>Voucher Details</h2>
          <div className={styles.voucherDetails}>
            <VoucherDetails
              selectedVouchers={selectedVouchers}
              editMode={false}
            />
          </div>
        </section>
        <div className="line" />
        <section className={styles.termsSection}>
          <TermsAndConditionsV2 />
        </section>
      </div>
 <Snakbar
        text={orderMessage}
        active={showSuccess && orderMessage}
        varient="success"
        customClass={styles.snackbarSuccess}
        hasButton={false}
        onClose={() => setShowSuccess(false)}
      />
    <Footer
        footerRef={footerRef}
        btnLabel={`Make Payment (S$${totalAmount})`}
        btnClick={handlePaymentClick}
        isFooterActive="true"
        disabled={!orderData}
        customClass={styles.footer}
        customClassForTerms={styles.terms}
      />
    </>
  );
};
